import { Card, CardContent, Box, Stack, Typography, Skeleton, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

const ACCENT_COLORS: Record<string, string> = {
  primary: '#BB86FC',
  secondary: '#03DAC6',
  success: '#81C995',
  warning: '#FFB74D',
  info: '#64B5F6',
  error: '#CF6679',
};

interface KpiCardProps {
  title: string;
  value: string;
  delta: string;
  deltaNote: string;
  subtitle: string;
  variant?: keyof typeof ACCENT_COLORS;
  loading?: boolean;
  sx?: SxProps<Theme>;

  accentColor?: string;
  valueColor?: string;
}

export function KpiCard({
  title,
  value,
  delta,
  deltaNote,
  subtitle,
  variant = 'primary',
  loading = false,
  sx,
  accentColor,
  valueColor,
}: KpiCardProps) {
  const topAccent = accentColor ?? ACCENT_COLORS[variant];
  const amountColor = valueColor ?? '#FFFFFF';

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 154,
        bgcolor: '#232323',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
        overflow: 'hidden',
        position: 'relative',
        borderRadius: 0.6,
        ...sx,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '0 0 auto 0',
          height: 3,
          bgcolor: topAccent,
        }}
      />

      <CardContent
        sx={{
          p: 3,
          pb: 2,
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Stack spacing={1} sx={{ height: '100%' }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'rgba(255,255,255,0.62)',
              letterSpacing: 1.2,
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1.35,
              minHeight: 16,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 400,
              color: amountColor,
              fontSize: 34,
              lineHeight: 1.15,
            }}
          >
            {loading ? <Skeleton width={120} height={40} /> : value}
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: 'rgba(129,201,149,0.95)',
              lineHeight: 1.35,
              minHeight: 20,
            }}
          >
            {loading ? (
              <Skeleton width={150} height={20} />
            ) : (
              <>
                {delta}
                {deltaNote ? (
                  <span style={{ color: 'rgba(255,255,255,0.62)', marginLeft: 6 }}>
                    {deltaNote}
                  </span>
                ) : null}
              </>
            )}
          </Typography>

          <Tooltip title={subtitle || ''} arrow placement="top">
            <Typography
              sx={{
                fontSize: 13,
                color: 'rgba(255,255,255,0.58)',
                lineHeight: 1.45,
                mt: 'auto',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                cursor: subtitle ? 'default' : 'inherit',
              }}
            >
              {loading ? <Skeleton width="80%" height={18} /> : subtitle}
            </Typography>
          </Tooltip>
        </Stack>
      </CardContent>
    </Card>
  );
}